<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useFormStore } from '@/stores/form'
import { useUserStore } from '@/stores/user'

declare global {
  interface Window {
    google: any
    initGoogleMaps: () => void
  }
}

const emit = defineEmits<{
  (e: 'submit'): void
}>()

const formStore = useFormStore()
const userStore = useUserStore()

const address = ref(formStore.data.address || '')
const zip = ref(formStore.data.zip || userStore.zip || '')
const city = ref(formStore.data.city || userStore.city || '')
const state = ref(formStore.data.state || userStore.state || '')

const showValidation = ref(false)
const addressInput = ref<HTMLInputElement | null>(null)

onMounted(() => {
  initAutocomplete()
})

function initAutocomplete() {
  if (!window.google?.maps?.places) {
    // Google Maps not loaded yet, will rely on manual entry
    return
  }

  if (!addressInput.value) return

  const autocomplete = new window.google.maps.places.Autocomplete(addressInput.value, {
    types: ['geocode'],
    componentRestrictions: { country: 'us' },
  })

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace()
    if (!place.address_components) return

    let streetNumber = ''
    let route = ''

    place.address_components.forEach((component: any) => {
      const type = component.types[0]
      switch (type) {
        case 'street_number':
          streetNumber = component.long_name
          break
        case 'route':
          route = component.long_name
          break
        case 'locality':
          city.value = component.long_name
          break
        case 'administrative_area_level_1':
          state.value = component.short_name
          break
        case 'postal_code':
          zip.value = component.long_name
          break
      }
    })

    address.value = `${streetNumber} ${route}`.trim()
  })
}

function handleSubmit() {
  if (!address.value.trim() || zip.value.length !== 5) {
    showValidation.value = true
    return
  }

  formStore.setField('address', address.value.trim())
  formStore.setField('zip', zip.value)
  formStore.setField('city', city.value)
  formStore.setField('state', state.value)
  emit('submit')
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    handleSubmit()
  }
}

function handleZipInput(e: Event) {
  const input = e.target as HTMLInputElement
  // Only allow digits
  zip.value = input.value.replace(/\D/g, '').slice(0, 5)
}
</script>

<template>
  <div class="w-full animate-slide-up">
    <div class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mx-1">
      <div class="space-y-4">
        <!-- Address -->
        <div>
          <input
            ref="addressInput"
            v-model="address"
            type="text"
            placeholder="Street Address"
            autocomplete="street-address"
            @keydown="handleKeydown"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base transition-colors',
              showValidation && !address.trim()
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
          <p v-if="showValidation && !address.trim()" class="text-red-500 text-xs mt-1 ml-1">
            Please enter your address
          </p>
        </div>

        <!-- City/State (auto-filled, read-only display) -->
        <div v-if="city || state" class="flex gap-3">
          <input
            v-model="city"
            type="text"
            placeholder="City"
            autocomplete="address-level2"
            class="flex-1 py-3.5 px-4 border-2 border-gray-200 rounded-xl text-base bg-gray-50"
          />
          <input
            v-model="state"
            type="text"
            placeholder="State"
            autocomplete="address-level1"
            class="w-20 py-3.5 px-4 border-2 border-gray-200 rounded-xl text-base text-center bg-gray-50"
          />
        </div>

        <!-- Zip Code -->
        <div>
          <input
            :value="zip"
            @input="handleZipInput"
            type="text"
            inputmode="numeric"
            placeholder="Zip Code"
            autocomplete="postal-code"
            maxlength="5"
            @keydown="handleKeydown"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base transition-colors',
              showValidation && zip.length !== 5
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
          <p v-if="showValidation && zip.length !== 5" class="text-red-500 text-xs mt-1 ml-1">
            Please enter a valid 5-digit zip code
          </p>
        </div>
      </div>

      <!-- Submit button -->
      <button
        @click="handleSubmit"
        class="w-full mt-5 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-[0.98] transition-all"
      >
        Continue
        <svg class="inline w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
</template>
